import { ReactNode, useId, useState } from 'react';
import ChevronDownIcon from '../../assets/icons/chevron_down.svg?react';
import {
  TimeRangeSelector,
  type CustomDateRange,
  type TimeRange,
} from '../../components/dialogs/TimeRangeSelector';
import { cn } from '../../utils/className';
import type { OutcomeShare } from './machineSimulation';
import {
  formatPercent,
  type AcceptRatePoint,
  type SortingView,
} from './sortingView';

/** Swatch per outcome: accepted first, then the ejectors in order. */
const OUTCOME_SWATCHES = [
  'bg-green-400',
  'bg-red-400',
  'bg-amber-300',
  'bg-blue-400',
];

interface Outcome extends OutcomeShare {
  swatch: string;
  /** Why an ejector fired. Its row expands to show them. */
  reasons?: OutcomeShare[];
}

const SIZE_TICKS = [0, 25, 50, 75, 100, 125, 150, 175, 200];
/** The size bands sum to 100%, so no band comes near the top of a 0-100 axis. */
const PERCENT_TICKS = [60, 45, 30, 15, 0];
const PERCENT_MAX = PERCENT_TICKS[0];
const RATE_TICKS = [100, 75, 50, 25, 0];

interface SortingTabFrameProps {
  timeRange: TimeRange;
  customRange: CustomDateRange | undefined;
  onRangeChange: (range: TimeRange) => void;
  onCustomRangeChange: (range: CustomDateRange | undefined) => void;
  /** Days the machine sorted on, marked in the date picker. */
  markedDays?: Date[];
  /** Shows the Live marker: the machine runs and the window reaches now. */
  live: boolean;
  children: ReactNode;
}

/** The Sorting tab around its charts: the time window and the Live marker. */
export function SortingTabFrame({
  timeRange,
  customRange,
  onRangeChange,
  onCustomRangeChange,
  markedDays,
  live,
  children,
}: SortingTabFrameProps) {
  return (
    <div
      className='bg-surface-focus-support flex flex-col gap-6 px-4 pt-4 pb-10'
    >
      <div className='flex items-center justify-between gap-2'>
        <TimeRangeSelector
          appearance='heading'
          selectedRange={timeRange}
          customRange={customRange}
          onRangeChange={onRangeChange}
          onCustomRangeChange={onCustomRangeChange}
          markedDays={markedDays}
        />
        {live && (
          <span
            className='size-caption text-secondary flex items-center gap-1.5'
          >
            <span
              aria-hidden
              className='h-2 w-2 rounded-full bg-green-400
                motion-safe:animate-pulse'
            />
            Live
          </span>
        )}
      </div>

      {children}
    </div>
  );
}

/** A card with one line of text, for an empty or failed window. */
export function SortingNotice({ children }: { children: ReactNode }) {
  return (
    <p
      className='bg-surface-primary border-border-secondary size-p1
        text-secondary rounded-xl border p-5 text-center'
    >
      {children}
    </p>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className='size-caption text-primary tracking-[0.08em] uppercase'>
      {children}
    </h3>
  );
}

export function SortingCharts({ view }: { view: SortingView }) {
  const outcomes: Outcome[] = [
    { label: 'Accepted', percent: view.accepted },
    ...view.ejectors,
  ].map((outcome, i) => ({
    ...outcome,
    swatch: OUTCOME_SWATCHES[i % OUTCOME_SWATCHES.length],
  }));
  const [accepted] = outcomes;

  return (
    <>
      <section className='flex flex-col gap-2'>
        <SectionTitle>Acceptance rate</SectionTitle>
        <div
          className='bg-surface-primary border-border-secondary flex flex-col
            gap-6 rounded-xl border p-5 shadow-[0_4px_12px_rgba(0,0,0,0.03)]'
        >
          <div className='flex flex-col gap-2'>
            <div className='flex items-end justify-between gap-4 font-bold'>
              <div>
                <p
                  className='text-[30px] leading-[1.15] tracking-[-1.2px]
                    text-green-600'
                >
                  {formatPercent(accepted.percent)}
                </p>
                <p className='size-p2 text-secondary'>Acceptance rate</p>
              </div>
              <div className='text-right'>
                <p className='size-p1 text-primary tabular-nums'>
                  {view.sorted}
                </p>
                <p className='size-p2 text-secondary'>Sorted</p>
              </div>
            </div>
            <div
              role='img'
              aria-label={outcomes
                .map((o) => `${o.label} ${formatPercent(o.percent)}`)
                .join(', ')}
              className='flex h-8 overflow-hidden rounded-lg'
            >
              {outcomes.map((outcome) => (
                <div
                  key={outcome.label}
                  className={`${outcome.swatch} transition-[flex-grow]
                  duration-700 ease-out motion-reduce:transition-none`}
                  style={{ flexGrow: outcome.percent, flexBasis: 0 }}
                />
              ))}
            </div>
          </div>

          <ul className='flex flex-col'>
            {outcomes.map((outcome) =>
              outcome.reasons ? (
                <EjectorRow key={outcome.label} outcome={outcome} />
              ) : (
                <li
                  key={outcome.label}
                  className='border-border-secondary flex items-center gap-2
                    border-b pr-6 pb-4'
                >
                  <OutcomeLabel outcome={outcome} />
                </li>
              )
            )}
          </ul>

          {view.missedEjections !== undefined && (
            <p
              className='size-caption text-secondary border-border-secondary
                -mt-2 flex justify-between gap-2 border-t pt-3 pr-6'
            >
              <span>Missed ejections</span>
              <span className='tabular-nums'>
                {`${view.missedEjections.toFixed(2).replace('.', ',')}%`}
              </span>
            </p>
          )}
        </div>
      </section>

      {view.acceptRateSeries && view.acceptRateSeries.length > 1 && (
        <section className='flex flex-col gap-2'>
          <SectionTitle>Acceptance rate over time</SectionTitle>
          <AcceptRateChart series={view.acceptRateSeries} />
        </section>
      )}

      {view.sizeBands && (
        <section className='flex flex-col gap-2'>
          <SectionTitle>Size distribution</SectionTitle>
          <SizeChart sizeBands={view.sizeBands} />
        </section>
      )}
    </>
  );
}

/** Labels a bucket start: the time of day, or the date for a window of days. */
function formatBucketTime(time: number, spanMs: number): string {
  const date = new Date(time);
  if (spanMs > 36 * 60 * 60_000) {
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
    });
  }
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function AcceptRateChart({ series }: { series: AcceptRatePoint[] }) {
  const first = series[0].time;
  const last = series[series.length - 1].time;
  const span = last - first;
  const middle = series[Math.floor(series.length / 2)].time;

  return (
    <figure
      className='bg-surface-primary border-border-secondary flex flex-col gap-4
        rounded-2xl border px-5 pt-4 pb-5'
    >
      <p className='size-caption text-primary font-bold'>Accepted</p>
      <div className='flex gap-3'>
        <div
          aria-hidden
          className='size-caption text-secondary flex h-[160px] flex-col
            items-end justify-between'
        >
          {RATE_TICKS.map((tick) => (
            <span key={tick} className='leading-none'>
              {tick === 0 ? '0' : `${tick}%`}
            </span>
          ))}
        </div>
        <div className='flex min-w-0 flex-1 flex-col gap-2'>
          <div className='relative h-[160px]'>
            <div
              aria-hidden
              className='border-border-secondary absolute inset-0 flex flex-col
                justify-between border-b'
            >
              {RATE_TICKS.slice(0, -1).map((tick) => (
                <div
                  key={tick}
                  className='border-border-secondary border-t border-dashed'
                />
              ))}
            </div>
            <div
              role='img'
              aria-label={series
                .map(
                  (point) =>
                    `${formatBucketTime(point.time, span)}: ${
                      point.percent === null
                        ? 'no sorting'
                        : formatPercent(point.percent)
                    }`
                )
                .join(', ')}
              className='absolute inset-0 flex items-end gap-0.5'
            >
              {series.map((point) => (
                <div
                  key={point.time}
                  title={
                    point.percent === null
                      ? `${formatBucketTime(point.time, span)}: no sorting`
                      : `${formatBucketTime(point.time, span)}: ${formatPercent(point.percent)}`
                  }
                  className='flex-1 rounded-t bg-green-400 transition-[height]
                    duration-700 ease-out motion-reduce:transition-none'
                  style={{ height: `${point.percent ?? 0}%` }}
                />
              ))}
            </div>
          </div>
          <div
            aria-hidden
            className='size-caption text-secondary flex justify-between'
          >
            <span>{formatBucketTime(first, span)}</span>
            <span>{formatBucketTime(middle, span)}</span>
            <span>{formatBucketTime(last, span)}</span>
          </div>
        </div>
      </div>
    </figure>
  );
}

function SizeChart({ sizeBands }: { sizeBands: number[] }) {
  return (
    <figure
      className='bg-surface-primary border-border-secondary flex flex-col gap-4
        rounded-2xl border px-5 pt-4 pb-5'
    >
      <p className='size-caption text-primary font-bold'>Percentage</p>
      <div className='flex gap-3'>
        <div
          aria-hidden
          className='size-caption text-secondary flex h-[218px] flex-col
            items-end justify-between'
        >
          {PERCENT_TICKS.map((tick) => (
            <span key={tick} className='leading-none'>
              {tick === 0 ? '0' : `${tick}%`}
            </span>
          ))}
        </div>
        <div className='flex min-w-0 flex-1 flex-col gap-2'>
          <div className='relative h-[218px]'>
            {/* Recessive dashed grid behind the bars. */}
            <div
              aria-hidden
              className='border-border-secondary absolute inset-0 flex flex-col
                justify-between border-b'
            >
              {PERCENT_TICKS.slice(0, -1).map((tick) => (
                <div
                  key={tick}
                  className='border-border-secondary border-t border-dashed'
                />
              ))}
            </div>
            <div
              role='img'
              aria-label={sizeBands
                .map(
                  (share, i) =>
                    `${SIZE_TICKS[i]} to ${SIZE_TICKS[i + 1]} mm: ${formatPercent(share)}`
                )
                .join(', ')}
              className='absolute inset-0 flex items-end gap-0.5'
            >
              {sizeBands.map((share, i) => (
                <div
                  key={SIZE_TICKS[i]}
                  title={`${SIZE_TICKS[i]}–${SIZE_TICKS[i + 1]} mm: ${formatPercent(share)}`}
                  className='flex-1 rounded-t bg-green-400 transition-[height]
                    duration-700 ease-out motion-reduce:transition-none'
                  style={{ height: `${(share / PERCENT_MAX) * 100}%` }}
                />
              ))}
            </div>
          </div>
          <div
            aria-hidden
            className='size-caption text-secondary flex justify-between'
          >
            {SIZE_TICKS.map((tick) => (
              <span key={tick}>{tick}</span>
            ))}
          </div>
          {/* In the plot's column, so it centres under the bars and not
              under the percentage labels as well. */}
          <p className='size-caption text-primary text-center font-bold'>
            Size in mm
          </p>
        </div>
      </div>
    </figure>
  );
}

function OutcomeLabel({ outcome }: { outcome: Outcome }) {
  return (
    <>
      <span
        aria-hidden
        className={`h-4 w-2 shrink-0 rounded-sm ${outcome.swatch}`}
      />
      <span className='size-p1 text-primary ml-1 flex-1 font-medium'>
        {outcome.label}
      </span>
      <span className='size-p1 text-primary w-12 text-right font-bold'>
        {formatPercent(outcome.percent)}
      </span>
    </>
  );
}

/** An ejector's row. It expands to show why the ejector fired. */
function EjectorRow({ outcome }: { outcome: Outcome }) {
  const [open, setOpen] = useState(false);
  const reasonsId = useId();

  return (
    <li
      className='border-border-secondary border-b py-4 last:border-b-0
        last:pb-0'
    >
      <button
        type='button'
        aria-expanded={open}
        aria-controls={reasonsId}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        className='flex w-full cursor-pointer items-center gap-2 text-left'
      >
        <OutcomeLabel outcome={outcome} />
        <ChevronDownIcon
          aria-hidden
          className={cn(
            `text-primary h-4 w-4 shrink-0 transition-transform duration-200
            ease-in-out motion-reduce:transition-none [&_path]:fill-current`,
            open && 'rotate-180'
          )}
        />
      </button>

      {/* Unfolds like the selection bar on the machine list. */}
      <div
        id={reasonsId}
        inert={!open}
        className={cn(
          `grid transition-[grid-template-rows,opacity] duration-200 ease-in-out
          motion-reduce:transition-none`,
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <ul className='flex min-h-0 flex-col gap-3 overflow-hidden pr-6 pl-5'>
          {outcome.reasons?.map((reason) => (
            <li key={reason.label} className='flex flex-col gap-1.5 first:pt-4'>
              <div className='size-p1 text-secondary flex justify-between gap-2'>
                <span>{reason.label}</span>
                <span>{formatPercent(reason.percent)}</span>
              </div>
              {/* The bar shows the reason's share of this ejector, in the
                  ejector's own colour: an ejector can have many reasons, more
                  than shades of one colour can tell apart. */}
              <div className='bg-border-secondary h-2 rounded-full'>
                <div
                  className={`h-full rounded-full ${outcome.swatch}`}
                  style={{
                    width: `${outcome.percent > 0 ? (reason.percent / outcome.percent) * 100 : 0}%`,
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
}
